import { useState, useEffect } from 'react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { JiraTicket } from '../types';

export const useJiraTickets = () => {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const [tickets, setTickets] = useState<JiraTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<JiraTicket | null>(null);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);

      const backendUrl = configApi.getString('backend.baseUrl');
      const [{ token }, profile] = await Promise.all([
        identityApi.getCredentials(),
        identityApi.getProfileInfo(),
      ]);
      const assigneeFilter = profile.email
        ? ` AND assignee = "${profile.email}"`
        : '';

      const response = await fetch(`${backendUrl}/api/custom-api/jira/client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          path: '/search',
          method: 'GET',
          params: {
            // jql: `project = CSD AND issuetype = "Project" AND status = "Pending Kickoff" AND labels not in ("Delete", "Program", "MDCMW", "No_Intake_Required")${assigneeFilter}`,
            jql: `project = CSD AND issuetype = "Project" AND status = "Pending Kickoff" AND labels not in ("Delete", "Program", "MDCMW", "No_Intake_Required")${assigneeFilter}`,
            maxResults: 100,
            fields: 'summary,status,priority,assignee,reporter,created,updated,description,labels',
          },
        }),
      });

      if (!response.ok)
        throw new Error(`Failed to fetch tickets: ${response.statusText}`);

      const data = await response.json();
      setTickets(
        data.issues?.map((issue: any) => ({
          id: issue.id,
          ticketNumber: issue.key,
          summary: issue.fields.summary,
          description: issue.fields.description ?? '',
          status: issue.fields.status.name,
          priority: issue.fields.priority?.name ?? 'Medium',
          assignee: issue.fields.assignee?.displayName ?? 'Unassigned',
          assigneeEmail: issue.fields.assignee?.emailAddress ?? '',
          reporter: issue.fields.reporter?.displayName ?? 'Unknown',
          createdAt: issue.fields.created,
          updatedAt: issue.fields.updated,
          labels: issue.fields.labels ?? [],
          acceptanceCriteria: [],
          hasWorkspace: false,
        })) || [],
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  return {
    tickets,
    loading,
    error,
    selectedTicket,
    refreshTickets: fetchTickets,
    selectTicket: (ticket: JiraTicket | null) => setSelectedTicket(ticket),
  };
};
